import React, { useState, useEffect } from 'react';
import { Building2, Edit, MapPin, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usersAPI, handleApiError } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { useFlashMessage } from '../components/FlashMessage';

const OwnerLocationManagement = () => {
  const { user } = useAuth();
  const { showDanger, FlashContainer } = useFlashMessage();
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLocations = async () => {
    setIsLoading(true);

    try {
      // Fetch locations from API endpoint
      const response = await usersAPI.getOwnerLocations();
      if (response.data.success) {
        setLocations(response.data.data);
      } else {
        setLocations([]);
      }
    } catch (err) {
      console.error('Failed to load locations:', err);
      showDanger(handleApiError(err));
      setLocations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getLocationTypeText = (type) => {
    switch (type) {
      case 'both': return 'Vale + Yıkama';
      case 'wash': return 'Oto Yıkama';
      case 'valet': return 'Vale';
      default: return type;
    }
  };

  const getLocationTypeColor = (type) => {
    switch (type) {
      case 'both': return 'bg-purple-100 text-purple-800';
      case 'wash': return 'bg-red-50 text-[#7a2c2c]';
      case 'valet': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '-';
    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length === 11) {
      return `${cleanPhone.slice(0, 4)} ${cleanPhone.slice(4, 7)} ${cleanPhone.slice(7, 9)} ${cleanPhone.slice(9, 11)}`;
    }
    return phone;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <FlashContainer />

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lokasyon Ayarları</h1>
          <p className="text-gray-600">Lokasyon bilgilerinizi görüntüleyin ve düzenleyin</p>
        </div>
      </div>

      {/* Desktop Table - Enterprise Grade with Visual Hierarchy */}
      <div className="hidden lg:block">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '20%' }} /> {/* Lokasyon - Primary Column */}
                <col style={{ width: '10%' }} /> {/* Tip - Narrow Label */}
                <col style={{ width: '35%' }} /> {/* Adres - Main Content Column */}
                <col style={{ width: '15%' }} /> {/* Telefon - Metadata */}
                <col style={{ width: '12%' }} /> {/* Vale Fiyatı - Numeric */}
                <col style={{ width: '8%' }} />  {/* İşlemler - Fixed Action */}
              </colgroup>
              <thead>
                <tr className="bg-gray-50">
                  <th scope="col" className="px-6 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Lokasyon
                  </th>
                  <th scope="col" className="px-5 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Tip
                  </th>
                  <th scope="col" className="px-5 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Adres
                  </th>
                  <th scope="col" className="px-5 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Telefon
                  </th>
                  <th scope="col" className="px-5 py-3.5 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Vale Fiyatı
                  </th>
                  <th scope="col" className="px-6 py-3.5 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {locations.map((location, idx) => (
                  <tr
                    key={location.id}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    {/* Lokasyon - Primary Column with Icon */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-gray-100 to-gray-50 rounded-lg flex items-center justify-center border border-gray-200">
                          <Building2 className="h-5 w-5 text-gray-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-gray-900 truncate">
                            {location.name}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Tip - Narrow Label Column */}
                    <td className="px-5 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${getLocationTypeColor(location.type)}`}>
                        {getLocationTypeText(location.type)}
                      </span>
                    </td>

                    {/* Adres - Main Content Column (Widest) */}
                    <td className="px-5 py-4">
                      <div className="text-sm text-gray-700 leading-5 line-clamp-2">
                        {location.address || (
                          <span className="text-gray-400 italic">Belirtilmemiş</span>
                        )}
                      </div>
                    </td>

                    {/* Telefon - Metadata Column */}
                    <td className="px-5 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-700 font-medium tabular-nums">
                        {formatPhoneNumber(location.phone) || (
                          <span className="text-gray-400 italic">-</span>
                        )}
                      </div>
                    </td>

                    {/* Vale Fiyatı - Numeric Column (Right Aligned) */}
                    <td className="px-5 py-4 whitespace-nowrap text-right">
                      <div className="text-sm font-semibold text-gray-900 tabular-nums">
                        ₺{parseFloat(location.default_valet_price || 0).toFixed(2)}
                      </div>
                    </td>

                    {/* İşlemler - Fixed Action Column */}
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <Link
                        to={`/owner/locations/edit/${location.id}`}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-[#7a2c2c] hover:text-[#5a1f1f] transition-colors"
                        title="Lokasyonu düzenle"
                      >
                        <Edit className="h-4 w-4" />
                        <span>Düzenle</span>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {locations.length === 0 && (
            <div className="text-center py-12 px-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                <Building2 className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">Lokasyon bulunamadı</h3>
              <p className="text-sm text-gray-600">
                Hesabınıza atanmış lokasyon bulunmuyor.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {locations.map((location) => (
          <div key={location.id} className="card">
            <div className="card-body">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <div className="h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-gray-600" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-semibold text-gray-900">{location.name}</h3>
                    <span className={`inline-block px-2 py-1 text-xs rounded-full ${getLocationTypeColor(location.type)}`}>
                      {getLocationTypeText(location.type)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-start">
                  <MapPin className="h-4 w-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-600">{location.address || '-'}</span>
                </div>

                <div className="flex items-center">
                  <Phone className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                  <span className="text-sm text-gray-600">{formatPhoneNumber(location.phone)}</span>
                </div>

                <div className="flex items-center">
                  <span className="text-sm text-gray-600">
                    Vale Fiyatı: <span className="font-medium">₺{parseFloat(location.default_valet_price || 0).toFixed(2)}</span>
                  </span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <Link
                  to={`/owner/locations/edit/${location.id}`}
                  className="btn-primary w-full flex items-center justify-center"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Düzenle
                </Link>
              </div>
            </div>
          </div>
        ))}

        {locations.length === 0 && (
          <div className="card">
            <div className="card-body text-center py-8">
              <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Lokasyon bulunamadı</h3>
              <p className="text-gray-600">
                Hesabınıza atanmış lokasyon bulunmuyor.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OwnerLocationManagement;
